import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Calculator, 
  FileText, 
  CheckCircle, 
  TrendingUp, 
  Clock, 
  Users,
  ChevronRight,
  Sparkles,
  Target
} from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div 
    className="card p-6 h-full"
    style={{ 
      background: "var(--background-200)", 
      borderColor: `${color}30`,
      borderLeftWidth: "3px",
      borderLeftColor: color
    }}
  >
    <div 
      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
      style={{ background: `${color}20` }}
    >
      <Icon size={24} style={{ color: color }} />
    </div>
    <h3 
      className="text-lg font-bold mb-2"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h3>
    <p style={{ color: "var(--text-200)", fontSize: "0.95rem", lineHeight: "1.6" }}>
      {description}
    </p>
  </div>
);

// Stat Card Component
const StatCard = ({ value, label, icon: Icon }) => (
  <div 
    className="card p-6 text-center"
    style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
  >
    <Icon size={32} style={{ color: "var(--primary-300)", margin: "0 auto 12px" }} />
    <div 
      className="text-3xl font-bold mb-1"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
    >
      {value}
    </div>
    <div style={{ color: "var(--text-200)", fontSize: "0.9rem" }}>{label}</div>
  </div>
);

// Process Step Component
const ProcessStep = ({ number, title, description, icon: Icon, color }) => (
  <div className="flex gap-6">
    <div className="flex flex-col items-center">
      <div 
        className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0"
        style={{ 
          background: color,
          color: "white",
          boxShadow: `0 4px 20px ${color}50`
        }}
      >
        <Icon size={20} />
      </div>
      <div 
        className="w-0.5 flex-grow mt-4"
        style={{ background: `linear-gradient(to bottom, ${color}, transparent)` }}
      />
    </div>
    <div className="pb-8 flex-grow">
      <h3 
        className="text-xl font-bold mb-2"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {title}
      </h3>
      <p style={{ color: "var(--text-200)", lineHeight: "1.6" }}>{description}</p>
    </div>
  </div>
);

// Main Valuation Page Component
const ValuationPage = () => {
  const features = [
    {
      icon: FileText,
      title: "Complete Summary",
      description: "Machine details, condition, photos, notes, and customer expectations all in one place.",
      color: "#0f5fff"
    },
    {
      icon: Sparkles,
      title: "AI Assistance",
      description: "Market data and comparable machines reviewed to support accuracy and consistency.",
      color: "#0049db"
    },
    {
      icon: Target,
      title: "Human Judgement",
      description: "Final decisions always remain with your dealership. AI supports, doesn't replace.",
      color: "#0038a8"
    },
    {
      icon: TrendingUp,
      title: "Faster Turnaround",
      description: "Complete information means reduced back-and-forth and quicker answers for salespeople.",
      color: "#0f5fff"
    }
  ];

  const process = [
    {
      number: 1,
      icon: FileText,
      color: "#0038a8",
      title: "Appraisal Submitted",
      description: "Salesperson completes guided appraisal with all required details, photos, and notes."
    },
    {
      number: 2,
      icon: Calculator,
      color: "#0049db",
      title: "Structured Summary Delivered",
      description: "Clear PDF report sent to valuation manager, controller, or principal with complete information."
    },
    {
      number: 3,
      icon: Sparkles,
      color: "#0f5fff",
      title: "AI Reviews Market Data",
      description: "Background analysis of comparable machines and market trends to support decision-making."
    },
    {
      number: 4,
      icon: CheckCircle,
      color: "#0f5fff",
      title: "Decision Made",
      description: "Valuation set and approved. Salesperson notified immediately to proceed with quote."
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative min-h-[60vh] flex items-center">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/app-valuation.jpg" 
              alt="Valuation interface"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.9) 0%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-16">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Calculator size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Valuation</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Valuation <span className="text-gradient">Process</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                The valuation process is where <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>good information</span> turns into{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>confident decisions</span>.
              </p>
            </div>
          </div>
        </section>

        {/* Overview Section with Image */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Image Side */}
              <div className="relative">
                <div 
                  className="rounded-2xl overflow-hidden"
                  style={{ boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)" }}
                >
                  <img 
                    src="https://www.machinerymarketed.com/app-valuation.jpg" 
                    alt="Valuation interface"
                    className="w-full h-auto"
                  />
                </div>
                {/* Floating Badge */}
                <div 
                  className="absolute -bottom-4 -right-4 p-4 rounded-2xl"
                  style={{ 
                    background: "var(--primary-100)",
                    boxShadow: "0 10px 30px rgba(15, 95, 255, 0.3)"
                  }}
                >
                  <Target size={40} style={{ color: "white" }} />
                </div>
              </div>
              
              {/* Content Side */}
              <div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                >
                  Complete Information, Clear Decisions
                </h2>
                <p 
                  className="text-lg mb-6"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  Once an appraisal is submitted, MachineryMarketed delivers a clear, structured summary 
                  to the person responsible for valuing trades — whether that's the used equipment manager, 
                  financial controller, or dealer principal.
                </p>
                <p 
                  className="text-lg"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  Everything required to make a sound decision is presented up front: machine details, 
                  condition, photos, notes, and customer expectations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Key Features
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                What makes the valuation process effective
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Valuation Journey
              </h2>
              
              <div className="bg-opacity-50 rounded-2xl p-8" style={{ background: "var(--background-200)" }}>
                {process.map((step, index) => (
                  <ProcessStep key={index} {...step} />
                ))}
                
                {/* Final Result */}
                <div className="flex gap-6">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--accent-100)" }}
                  >
                    <TrendingUp size={20} style={{ color: "var(--background-100)" }} />
                  </div>
                  <div>
                    <h3 
                      className="text-xl font-bold mb-2"
                      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                    >
                      Stronger Deals
                    </h3>
                    <p style={{ color: "var(--text-200)" }}>
                      Faster answers improve negotiation strength and deal conversion.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="text-3xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Impact
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <StatCard 
                  icon={Clock}
                  value="Faster"
                  label="Reduced turnaround times"
                />
                <StatCard 
                  icon={Users}
                  value="Less"
                  label="Internal back-and-forth"
                />
                <StatCard 
                  icon={TrendingUp}
                  value="Higher"
                  label="Deal conversion rates"
                />
              </div>
            </div>
          </div>
        </section>

        {/* AI Section with Image */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div>
                <div 
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-4"
                  style={{ background: "rgba(15, 95, 255, 0.2)", color: "var(--primary-300)" }}
                >
                  <Sparkles size={16} />
                  AI-Powered
                </div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                >
                  Smart Support, Human Decisions
                </h2>
                <p 
                  className="text-lg mb-4"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  AI assists quietly in the background by reviewing market data and comparable machines, 
                  helping support accuracy and consistency without removing human judgement.
                </p>
                <p 
                  className="text-lg"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  Final decisions always remain with your dealership.
                </p>
              </div>
              
              <div className="relative">
                <div 
                  className="rounded-2xl overflow-hidden"
                  style={{ boxShadow: "0 20px 60px rgba(15, 95, 255, 0.2)" }}
                >
                  <img 
                    src="https://www.machinerymarketed.com/app-create-account.jpg" 
                    alt="App interface"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden max-w-4xl mx-auto"
            >
              {/* Background */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)"
                }}
              />
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}
                >
                  Ready for Better Valuations?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  See how MachineryMarketed can streamline your valuation process.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link 
                    to="/how-it-works" 
                    className="px-6 py-3 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "transparent", 
                      border: "2px solid white",
                      color: "white"
                    }}
                  >
                    See Full Process
                  </Link>
                  <Link 
                    to="/#contact" 
                    className="px-6 py-3 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "white", 
                      color: "var(--primary-100)"
                    }}
                  >
                    Request a Demo <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ValuationPage;
